import {jsx} from 'panel';
import kebabCase from 'lodash/kebabCase';

import {FilterPartsProps} from 'common/query-builder/query-entry-section/row-types/filter/parts/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {
  BooleanOperator,
  FilterOperator,
  FilterValue,
  ListQuantifier,
} from 'common/widgets/property-filter-menu/models/types';
import {
  applyFilterOperatorToFilter,
  getFilterOperatorsSections,
  getFilterOperatorSymbol,
  getListQuantifierSections,
} from 'common/query-builder/query-entry-section/row-types/filter/utils';
import {hasComputedListFilters, isEnhancedListFilter} from 'common/widgets/property-filter-menu/utils';
// eslint-disable-next-line import/no-cycle -- value component can render filter parts recursively
import {
  FilterListValueTemplate,
  FilterValueTemplate,
} from 'common/query-builder/query-entry-section/row-types/filter/types/index';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {Placement} from 'common/components/tooltip/types';
import {ChangeEvent} from 'common/components/select/types';
import {SectionItem} from 'common/components/items-menu/types';
import {TypecastSubitem} from 'common/query-builder/query-entry-section/components/typecast-subitem';

// defines which filter types do not render an operator selector
// this is a temporal check until we can bring all the new filter
// UI to all filter types
const NO_OPERATOR_TYPES = new Set<PropertyType>([PropertyType.Object, PropertyType.NonProperty]);

export function FilterParts({
  filter,
  eventNames,
  projectId,
  workspaceId,
  userId,
  dataGroupId,
  hasNewUI,
  hideListQuantifier = false,
  operatorOverrides,
  updateEntry,
  isValueActive,
  setActiveEntry,
  readonly,
  withQuickDelete,
  withQuickRename,
  onChangeOperator,
  onChangeValue,
  onValueSelectMenuChange,
  rowActions,
  showTypecastControl,
  unit,
  disabledTypecastTypes,
  hasPlaceholder,
}: FilterPartsProps) {
  const hasProperty = filter.hasSelectedProperty();
  const isCohortFilter = filter.resourceType === ResourceType.Cohort;

  const changeOperator = (operator: FilterOperator) => {
    const newFilter = applyFilterOperatorToFilter(operator, filter);

    updateEntry(newFilter);
    onChangeOperator?.(operator);
  };

  const changeValue = (value: FilterValue) => {
    if (filter.propertyType === PropertyType.Boolean) {
      // changing the operator for boolean values also handles changing the value
      changeOperator(value ? BooleanOperator.True : BooleanOperator.False);
    } else {
      updateEntry(filter.setAttribute(`filterValue`, value));
    }

    onChangeValue?.(value);
  };

  const showListQuantifier =
    isEnhancedListFilter(filter) && hideListQuantifier === false && Boolean(hasComputedListFilters(filter)) === false;

  const FilterValue =
    (isEnhancedListFilter(filter) && FilterListValueTemplate[filter.propertyType]) ||
    FilterValueTemplate[filter.propertyType];

  return (
    <div
      sel='.mp-query-entry-filter-parts'
      class={{'with-quick-delete': withQuickDelete, 'with-quick-rename': withQuickRename}}
    >
      {/** enhanced list property filters define a quantifier operator for items in the list */}
      {showListQuantifier && (
        <div sel='.mp-query-entry-filter-parts-row'>
          <SelectEl
            attrs={{
              elref: `listFilterQuantifier`,
              'menu-width': 200,
              'always-show-caret': hasNewUI,
              theme: hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select,
              tooltip: {placement: Placement.Top},
              readonly,
            }}
            props={{sections: getListQuantifierSections(filter)}}
            on={{
              change: ({detail}: ChangeEvent<SectionItem<ListQuantifier>>) => {
                updateEntry(filter.setAttribute(`listQuantifier`, detail.item.value));
              },
            }}
          />
        </div>
      )}

      <div sel='.mp-query-entry-filter-parts-row'>
        {hasProperty &&
          !isCohortFilter &&
          !NO_OPERATOR_TYPES.has(filter.propertyType) &&
          // newUI uses a radio-button selection specified in FilterValueTemplate for boolean values instead of a SelectEl
          !(hasNewUI && filter.propertyType === PropertyType.Boolean) && (
            <SelectEl
              attrs={{
                elref: `filterOperator`,
                'disable-search': true,
                'menu-width': 200,
                'menu-height': null,
                readonly,
                'selected-option':
                  operatorOverrides?.find(({value}) => value === filter.filterOperator)?.label ??
                  getFilterOperatorSymbol(filter),
                theme: hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select,
                'never-show-caret': true,
                'disable-tooltip': true,
              }}
              class={{
                [`for-${filter.propertyType}`]: true,
                [`for-${kebabCase(filter.filterOperator)}-operator`]: true,
              }}
              props={{sections: getFilterOperatorsSections(filter, operatorOverrides)}}
              on={{
                change: (ev: ChangeEvent) => changeOperator(ev.detail.item.value[0]),
              }}
            />
          )}

        {/** render value selection only for non-cohort filters */}
        {!isCohortFilter && FilterValue && (
          <FilterValue
            {...{
              filter,
              eventNames,
              dataGroupId,
              projectId,
              workspaceId,
              userId,
              readonly,
              hasNewUI,
              rowActions,
              hasPlaceholder,
            }}
            isActive={isValueActive}
            onChange={(value) => changeValue(value)}
            onSelectMenuChange={(ev) => onValueSelectMenuChange?.(ev)}
            setActiveEntry={(state) => setActiveEntry?.(state)}
            updateEntry={(filter) => updateEntry(filter)}
          />
        )}
        {unit && <div sel='.filter-value-unit'>{unit}</div>}
      </div>

      {hasNewUI && showTypecastControl && (
        <TypecastSubitem value={filter} onTypeChange={updateEntry} disabledTypes={disabledTypecastTypes} forFilter />
      )}
    </div>
  );
}
